<template>
    <product-card-layout
        :thumb="product.info.imageUrl"
        :name="product.info.name"
        :product-link="productLink"
        :offers-count="product.offers && product.offers.length"
        :is-recommended="product.recommended"
        :_expanded-count="expanded"
        :_expanded-limit="expandedLimit"
        @change-expanded-count="expanded = $event"
        @change-expanded-limit="expandedLimit = $event"
    >
        <template v-slot>
            <div>
                <div>
                    <nuxt-link
                        v-if="!isAddOn"
                        no-prefetch
                        :to="productLink"
                        class="text-decoration-none font-weight-medium"
                    >
                        {{ product.info.name }}
                    </nuxt-link>
                    <span v-else class="text-decoration-none font-weight-medium">
                        {{ product.info.name }}
                    </span>
                    <v-chip
                        v-if="minPriceOffer.transferType && minPriceOffer.transferType !== 'UNKNOWN'"
                        label
                        size="x-small"
                        class="ms-2"
                    >
                        {{ $t(`filters_transferType.${minPriceOffer.transferType}`) }}
                    </v-chip>
                    <ProductVariantLabel :product="product" class="ms-1" />
                </div>
                <supplier-name-label v-if="isTO1" :supplier-name="product.info.supplierName" />
                <div v-if="product.info.description" class="short-description mt-1">
                    {{ product.info.description }}
                </div>
                <transfer-driver-languages class="mt-1" :lang-codes="langCodes" />
                <div><slot /></div>
            </div>
        </template>
        <template v-if="minPriceOffer" v-slot:priceCol>
            <slot name="price" :offer="minPriceOffer" :offer-key="minPriceOffer.offerKey">
                <template v-if="!minPriceOffer.soldOut">
                    <transfer-card-price-block
                        v-if="showPriceDuringSearch && product.offers[0].price && !hidePrice"
                        :offer="minPriceOffer"
                    />
                    <v-btn
                        v-if="isReturnTransfer"
                        variant="flat"
                        block
                        color="primary"
                        class="my-2"
                        @click="selectTransferOffer(product.offers[0])"
                    >
                        {{ $t('select') }}
                    </v-btn>
                    <v-btn v-else variant="flat" block color="primary" class="my-2" :to="productLink">
                        {{ $t('book') }}
                    </v-btn>
                </template>
            </slot>
            <div class="text-primary cursor-pointer text-no-wrap" @click="toggleOffers">
                <span>
                    {{ expanded ? $t('hide') : $t('show') }}
                </span>
                <v-icon color="primary">
                    {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
            </div>
        </template>
        <template v-if="product.offers.length" v-slot:offers>
            <div v-if="expanded">
                <transfer-offer-card
                    v-for="(offer, offerIndex) in product.offers.slice(0, expanded)"
                    :key="offer.offerKey"
                    :product-id="product.productId"
                    :offer="offer"
                    :is-return-transfer="isReturnTransfer"
                    :full-card="false"
                    :data-cy="`transfer-offer-card-${offerIndex}`"
                    :is-add-on="isAddOn"
                    :compare-mode="compareMode"
                    @select-transfer-offer="selectTransferOffer"
                >
                    <template v-slot:price>
                        <slot name="price" :offer="offer" :offer-key="offer.offerKey" />
                    </template>
                </transfer-offer-card>
                <v-list-item
                    v-if="expanded === expandedLimit && product.offers.length > expandedLimit"
                    density="compact"
                    class="d-flex justify-center"
                    @click.stop="expanded = product.offers.length"
                >
                    <v-icon>mdi-chevron-down</v-icon>
                </v-list-item>
            </div>
        </template>
    </product-card-layout>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Setup, Vue} from 'vue-facing-decorator'
    import TransferOfferCard from '~src/components/transfers/info/transferOfferCard.src'
    import TransferDriverLanguages from '@/components/snippets/TransferDriverLanguages.vue'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import TransferOperatedByLabel from '@/components/transfers/snippets/TransferOperatedByLabel.vue'
    import ProductCardLayout from '@/components/search/ProductCardLayout.vue'
    import TransferCardPriceBlock from '@/components/transfers/search/offers/TransferCardPriceBlock.vue'
    import ProductCardLayoutMobile from '@/components/search/ProductCardLayoutMobile.vue'
    import {minPriceOffer} from '~/utils/sort-helpers'
    import ProductVariantLabel from '~/components/snippets/ProductVariantLabel.vue'

    @Component({
        components: {
            ProductVariantLabel,
            ProductCardLayoutMobile,
            TransferCardPriceBlock,
            ProductCardLayout,
            TransferOperatedByLabel,
            TransferDriverLanguages,
            TransferOfferCard,
            SupplierNameLabel,
        },
    })
    export class TransferCard extends Vue {
        @Prop({default: () => []}) product
        @Prop() isReturnTransfer
        @Prop() isFirst
        @Prop({default: false, type: Boolean}) hidePrice
        @Prop({default: false, type: Boolean}) isAddOn
        @Prop({default: false, type: Boolean}) compareMode
        @Setup(() => useBookingPriceButton().showPriceDuringSearch) showPriceDuringSearch

        expanded = 0
        imgError = false
        expandedLimit = 3

        mounted() {
            if (this.$conf.public.expandOnClickAllTransfers === true) {
                this.expandedLimit = this.product?.offers?.length
            }
            if (this.isReturnTransfer && this.isFirst) {
                this.expanded = this.expandedLimit
            }
        }

        toggleOffers() {
            let expanded = 0
            if (!this.expanded) {
                expanded = this.expandedLimit
            }
            this.expanded = expanded
        }

        @Emit()
        selectTransferOffer(offer) {
            return offer
        }

        get langCodes() {
            return this.product.offers.reduce((arr, e) => {
                e.langCodes.forEach(lang => {
                    if (!arr.includes(lang)) {
                        arr.push(lang)
                    }
                })
                return arr
            }, [])
        }

        get minPriceOffer() {
            return minPriceOffer(this.product)
        }

        get productLink() {
            return transfersRuntimeStore.infoPageLink(this.product, transfersStore.searchRequest)
        }

        get isTO1() {
            return authStore.isTO1
        }
    }

    export default TransferCard
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';

    .offer-price {
        width: 20%;
    }

    @media #{map.get(settings.$display-breakpoints, 'xs')} {
        .offer-price {
            width: 25%;
        }
    }

    .wrap-words {
        white-space: normal;
    }

    .short-description {
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
</style>
